import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Briefcase, TrendingUp, Shield, Zap, 
  Wallet, BarChart3, ArrowUpRight, ArrowDownRight,
  Plus, Sparkles, Clock, Activity
} from 'lucide-react';
import WalletManager from '../components/portfolio/WalletManager';
import PortfolioAnalytics from '../components/portfolio/PortfolioAnalytics';
import HoldingsList from '../components/portfolio/HoldingsList';
import UniswapWidget from '../components/portfolio/UniswapWidget';
import PortfolioSummary from '../components/portfolio/PortfolioSummary';

interface WalletData {
  id: string;
  address: string;
  name: string;
  type: 'ETH' | 'BTC';
  balance: number;
  usdValue: number;
}

const PortfolioPageV2: React.FC = () => {
  const [wallets, setWallets] = useState<WalletData[]>([]);
  const [activeTab, setActiveTab] = useState<'overview' | 'holdings' | 'analytics' | 'swap'>('overview');
  const [isLoading, setIsLoading] = useState(true);
  
  useEffect(() => {
    // Simulate loading
    setTimeout(() => setIsLoading(false), 1000);
  }, []);

  // Mock data
  const [portfolioData] = useState({
    totalValue: 125420.50,
    change24h: 5.43,
    change7d: 12.87,
    change30d: -3.21,
    holdings: [
      { symbol: 'ETH', value: 45000, percentage: 35.9, color: '#627EEA' },
      { symbol: 'BTC', value: 38000, percentage: 30.3, color: '#F7931A' },
      { symbol: 'USDC', value: 15000, percentage: 12.0, color: '#2775CA' },
      { symbol: 'WBTC', value: 12420, percentage: 9.9, color: '#F09242' },
      { symbol: 'DAI', value: 10000, percentage: 8.0, color: '#F5AC37' },
      { symbol: 'Others', value: 5000.50, percentage: 3.9, color: '#8B5CF6' },
    ],
    performanceData: [
      { date: 'Jan 1', value: 100000 },
      { date: 'Jan 7', value: 105000 },
      { date: 'Jan 14', value: 98000 },
      { date: 'Jan 21', value: 110000 },
      { date: 'Jan 28', value: 115000 },
      { date: 'Feb 4', value: 108000 },
      { date: 'Feb 11', value: 118000 },
      { date: 'Feb 18', value: 125420 },
    ],
  });

  const [holdings] = useState([
    {
      id: '1',
      symbol: 'ETH',
      name: 'Ethereum',
      amount: 15.5,
      avgBuyPrice: 2200,
      currentPrice: 2903.23,
      value: 45000,
      pnl: 10896.57,
      pnlPercentage: 31.96,
      change24h: 5.2,
      allocation: 35.9,
      logo: '/coin-logos/ethereum.png',
      transactions: [
        { date: '2024-01-15', type: 'buy' as const, amount: 10, price: 2100, value: 21000 },
        { date: '2024-01-28', type: 'buy' as const, amount: 5.5, price: 2381, value: 13095.50 },
      ],
    },
    {
      id: '2',
      symbol: 'BTC',
      name: 'Bitcoin',
      amount: 0.85,
      avgBuyPrice: 38000,
      currentPrice: 44705.88,
      value: 38000,
      pnl: 5700,
      pnlPercentage: 17.65,
      change24h: 3.8,
      allocation: 30.3,
      logo: '/coin-logos/bitcoin.png',
      transactions: [
        { date: '2024-01-10', type: 'buy' as const, amount: 0.5, price: 36000, value: 18000 },
        { date: '2024-02-01', type: 'buy' as const, amount: 0.35, price: 40000, value: 14000 },
      ],
    },
    {
      id: '3',
      symbol: 'USDC',
      name: 'USD Coin',
      amount: 15000,
      avgBuyPrice: 1,
      currentPrice: 1,
      value: 15000,
      pnl: 0,
      pnlPercentage: 0,
      change24h: 0.01,
      allocation: 12.0,
      logo: '/coin-logos/usd-coin.png',
      transactions: [
        { date: '2024-02-10', type: 'buy' as const, amount: 15000, price: 1, value: 15000 },
      ],
    },
  ]);

  const [portfolioSummary] = useState({
    riskScore: 45,
    diversificationScore: 78,
    performanceRank: 85,
    totalTransactions: 24,
    winRate: 67.5,
    bestPerformer: { symbol: 'ETH', percentage: 31.96 },
    worstPerformer: { symbol: 'USDC', percentage: 0 },
  });

  const handleAddWallet = (wallet: Omit<WalletData, 'id'>) => {
    const newWallet = {
      ...wallet,
      id: Date.now().toString(),
    };
    setWallets([...wallets, newWallet]);
  };

  const handleRemoveWallet = (id: string) => {
    setWallets(wallets.filter(w => w.id !== id));
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'holdings', label: 'Holdings', icon: Briefcase },
    { id: 'analytics', label: 'Analytics', icon: Activity },
    { id: 'swap', label: 'Swap', icon: Zap },
  ];

  const QuickStat = ({ title, value, change, icon: Icon, trend }: any) => (
    <motion.div
      whileHover={{ y: -4, transition: { duration: 0.2 } }}
      className="bg-gradient-to-br from-zinc-900/90 to-zinc-900/50 backdrop-blur-sm border border-zinc-800 rounded-xl p-6 relative overflow-hidden group"
    >
      <div className="absolute inset-0 bg-gradient-to-br from-violet-600/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      
      <div className="relative z-10">
        <div className="flex items-center justify-between mb-4">
          <span className="text-sm font-medium text-zinc-400">{title}</span>
          <Icon className="w-5 h-5 text-violet-400" />
        </div>
        
        <div className="space-y-2">
          <div className="text-2xl font-bold text-white">{value}</div>
          {change !== undefined && (
            <div className={`flex items-center gap-1.5 text-sm font-medium ${
              change >= 0 ? 'text-green-400' : 'text-red-400'
            }`}>
              {trend === 'up' ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
              <span>{Math.abs(change).toFixed(2)}%</span>
              <span className="text-zinc-500">24h</span>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        <div className="flex items-center justify-center h-96">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            className="w-12 h-12 border-3 border-violet-500 border-t-transparent rounded-full"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div className="flex items-center gap-4">
              <motion.div 
                whileHover={{ rotate: 10 }}
                className="inline-flex items-center justify-center w-14 h-14 bg-gradient-to-br from-violet-500 to-purple-600 rounded-2xl shadow-lg"
              >
                <Briefcase className="w-7 h-7 text-white" />
              </motion.div>
              <div>
                <h1 className="text-3xl font-bold text-white flex items-center gap-2">
                  Portfolio Tracker
                  <Sparkles className="w-6 h-6 text-amber-400" />
                </h1>
                <p className="text-zinc-400 mt-1">Monitor and manage your crypto investments</p>
              </div>
            </div>
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white px-6 py-3 rounded-xl font-medium flex items-center gap-2 shadow-lg shadow-violet-600/25 transition-all"
            >
              <Plus className="w-5 h-5" />
              Add Transaction
            </motion.button>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <QuickStat
              title="Total Value"
              value={`$${portfolioData.totalValue.toLocaleString()}`}
              change={portfolioData.change24h}
              icon={Wallet}
              trend="up"
            />
            <QuickStat
              title="Today's P&L"
              value={`+$${(portfolioData.totalValue * 0.0543).toFixed(2)}`}
              change={5.43}
              icon={TrendingUp}
              trend="up"
            />
            <QuickStat
              title="Best Performer"
              value="ETH"
              change={31.96}
              icon={Zap}
              trend="up"
            />
            <QuickStat
              title="Portfolio Health"
              value="Good"
              icon={Shield}
            />
          </div>

          {/* Tabs */}
          <div className="flex gap-2 overflow-x-auto pb-2">
            {tabs.map((tab) => (
              <motion.button
                key={tab.id}
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'bg-violet-600 text-white shadow-lg shadow-violet-600/25'
                    : 'bg-zinc-800/50 text-zinc-400 hover:bg-zinc-800 hover:text-white'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </motion.button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <AnimatePresence mode="wait">
          {activeTab === 'overview' && (
            <motion.div
              key="overview"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
              className="space-y-6"
            >
              <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                <div className="xl:col-span-2">
                  <PortfolioAnalytics {...portfolioData} />
                </div>
                <div className="xl:col-span-1">
                  <WalletManager
                    wallets={wallets}
                    onAddWallet={handleAddWallet}
                    onRemoveWallet={handleRemoveWallet}
                  />
                </div>
              </div>
              
              <PortfolioSummary {...portfolioSummary} />
            </motion.div>
          )}

          {activeTab === 'holdings' && (
            <motion.div
              key="holdings"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              <HoldingsList holdings={holdings} />
            </motion.div>
          )}

          {activeTab === 'analytics' && (
            <motion.div
              key="analytics"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
              className="space-y-6"
            >
              <PortfolioAnalytics {...portfolioData} />
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <PortfolioSummary {...portfolioSummary} />
                <div className="bg-zinc-900/50 backdrop-blur-sm border border-zinc-800 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <Clock className="w-5 h-5 text-violet-400" />
                    Recent Activity
                  </h3>
                  <div className="space-y-3">
                    {holdings.flatMap(h => 
                      h.transactions.map((tx, i) => (
                        <div key={`${h.id}-${i}`} className="flex items-center justify-between bg-zinc-800/50 rounded-lg p-3">
                          <div className="flex items-center gap-3">
                            <img src={h.logo} alt={h.symbol} className="w-8 h-8 rounded-full" />
                            <div>
                              <div className="text-sm font-medium text-white">
                                {tx.type === 'buy' ? 'Bought' : 'Sold'} {tx.amount} {h.symbol}
                              </div>
                              <div className="text-xs text-zinc-400">{tx.date}</div>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-sm font-medium text-white">${tx.value.toLocaleString()}</div>
                            <div className="text-xs text-zinc-400">@ ${tx.price.toLocaleString()}</div>
                          </div>
                        </div>
                      ))
                    ).slice(0, 5)}
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {activeTab === 'swap' && (
            <motion.div
              key="swap"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
              className="flex justify-center"
            >
              <div className="w-full max-w-xl">
                <UniswapWidget />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
};

export default PortfolioPageV2;